/**
 * documents/utils — pure helpers extracted for testability.
 *
 * These helpers back specific CP2 fixes so a jest test can lock in
 * the behaviour without needing a full React Testing Library setup.
 */

/**
 * Format the "X of Y" quota usage string used in the storage strip.
 *
 * When bytes_used < 1 MB we render KB with 0-decimal precision so
 * tiny files (a handful of KB) don't misleadingly round to "0.0 MB".
 * When bytes_used >= 1 MB we render MB with 1-decimal precision.
 *
 * The denominator ALWAYS renders in MB (the caps are configured in
 * MB, so switching units would confuse the visual hierarchy).
 */
export function formatQuotaUsage(bytesUsed, quotaBytes) {
  const bu = Number(bytesUsed) || 0;
  const qb = Number(quotaBytes) || 0;
  const denomMb = (qb / (1024 * 1024)).toFixed(0);
  if (bu < 1024 * 1024) {
    return `${(bu / 1024).toFixed(0)} KB of ${denomMb} MB`;
  }
  return `${(bu / (1024 * 1024)).toFixed(1)} MB of ${denomMb} MB`;
}

/**
 * Pick the default doc_type for the upload widget's `<select>`.
 *
 * The widget mounts BEFORE `/api/documents/taxonomy` resolves, so
 * `useState(docTypes[0])` captures an empty initial value. This
 * helper is called from a `useEffect(docTypes)` and returns the
 * next value the state should carry:
 *
 *   - If `docTypes` is empty → return "" (no valid choice yet).
 *   - If `current` is empty → return `docTypes[0]`.
 *   - If `current` is no longer in `docTypes` → return `docTypes[0]`.
 *   - Otherwise → return `current` (no change; useEffect can skip
 *     the setState).
 */
export function pickDefaultDocType(docTypes, current) {
  if (!Array.isArray(docTypes) || docTypes.length === 0) return "";
  if (!current) return docTypes[0];
  if (!docTypes.includes(current)) return docTypes[0];
  return current;
}
